/**
 * PWA Offline — useOfflineSync Hook
 *
 * Design decisions (from design.md Section 6):
 * - useOfflineSync() returns { sync, pendingCount, isSyncing, lastError, conflict, onKeepServer, onKeepLocal }
 * - Calls SyncManager.startFifo() on manual trigger
 * - Handles conflict state from SyncManager FIFO
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '../db';
import { SyncManager, type SyncConflict } from '../SyncManager';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UseOfflineSyncResult {
    sync: () => Promise<void>;
    pendingCount: number;
    isSyncing: boolean;
    lastError: string | null;
    conflict: SyncConflict | null;
    onKeepServer: (writeId: number) => Promise<void>;
    onKeepLocal: (writeId: number, payload: Record<string, unknown>) => Promise<void>;
    clearConflict: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useOfflineSync(): UseOfflineSyncResult {
    const [pendingCount, setPendingCount] = useState<number>(0);
    const [isSyncing, setIsSyncing] = useState<boolean>(false);
    const [lastError, setLastError] = useState<string | null>(null);
    const [conflict, setConflict] = useState<SyncConflict | null>(null);

    // ── Refresh pending count ─────────────────────────────────────────────

    const refreshPendingCount = useCallback(async () => {
        try {
            const count = await db.pending_writes
                .where('status')
                .equals('pending')
                .count();
            setPendingCount(count);
        } catch (err) {
            console.error('[useOfflineSync] Failed to refresh pending count:', err);
        }
    }, []);

    // ── Trigger sync ─────────────────────────────────────────────────────

    const sync = useCallback(async () => {
        if (isSyncing) {
            return; // Prevent concurrent syncs
        }

        try {
            setIsSyncing(true);
            setLastError(null);

            // Call SyncManager.startFifo() (Work Unit 4)
            const result = await SyncManager.startFifo();

            // If there are conflicts, surface the first one to the ConflictModal
            if (result.conflicts.length > 0) {
                setConflict(result.conflicts[0]);
            }

            // Refresh pending count after sync
            await refreshPendingCount();
        } catch (err) {
            console.error('[useOfflineSync] Sync failed:', err);
            setLastError(err instanceof Error ? err.message : 'Sync failed');
        } finally {
            setIsSyncing(false);
        }
    }, [isSyncing, refreshPendingCount]);

    // ── Conflict resolution ─────────────────────────────────────────────────

    /**
     * Keep server — discard local changes, delete the pending write.
     */
    const onKeepServer = useCallback(async (writeId: number) => {
        try {
            await db.pending_writes.delete(writeId);
        } catch (err) {
            console.error('[useOfflineSync] onKeepServer failed:', err);
        } finally {
            setConflict(null);
        }
    }, []);

    /**
     * Keep local — retry with X-Force-Update header.
     */
    const onKeepLocal = useCallback(
        async (writeId: number, payload: Record<string, unknown>) => {
            try {
                await SyncManager.retryWithForce(writeId, payload);
            } catch (err) {
                console.error('[useOfflineSync] onKeepLocal failed:', err);
            } finally {
                setConflict(null);
            }
        },
        []
    );

    const clearConflict = useCallback(() => {
        setConflict(null);
    }, []);

    // ── Initial load ──────────────────────────────────────────────────────

    useEffect(() => {
        refreshPendingCount();
    }, [refreshPendingCount]);

    return {
        sync,
        pendingCount,
        isSyncing,
        lastError,
        conflict,
        onKeepServer,
        onKeepLocal,
        clearConflict,
    };
}
